import { html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

export interface TrackInfo {
    index: number;
    name: string;
    /** True when the track carries at least one non-empty lyric syllable. Gates the "lyrics only" toggle. */
    hasLyrics?: boolean;
}

/**
 * Tracks control with dropdown for visibility toggles and per-track volume.
 *
 * @fires track-visibility-change - Fired when track visibility changes (detail: { visibleIndexes: number[] })
 * @fires track-volume-change - Fired when a track's volume changes (detail: { trackIndex: number, volume: number })
 * @fires track-mute-change - Fired when a track is muted/unmuted (detail: { trackIndex: number, muted: boolean })
 * @fires track-lyrics-only-change - Fired when a track's "show lyrics only" toggle changes (detail: { trackIndex: number, lyricsOnly: boolean })
 */
@customElement('tracks-control')
export class TracksControl extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-block;
                position: relative;
            }

            .toggle-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.4rem;
                padding: 0.5rem 0.75rem;
                background: transparent;
                border: 1px solid var(--border-color);
                border-radius: 6px;
                cursor: pointer;
                font-size: 0.85rem;
                font-weight: 500;
                color: var(--text-secondary, #cbd5e1);
                transition: all 0.2s;
                white-space: nowrap;
            }

            .toggle-btn:hover {
                background: var(--surface-light, rgba(255, 255, 255, 0.1));
                color: var(--text-primary, #f1f5f9);
            }

            .toggle-btn.active {
                background: var(--primary-color, #2563eb);
                color: white;
                border-color: var(--primary-color, #2563eb);
            }

            .tracks-icon {
                font-size: 1.1rem;
            }

            .tracks-arrow {
                font-size: 0.625rem;
                transition: transform 0.2s;
            }

            .toggle-btn.active .tracks-arrow {
                transform: rotate(180deg);
            }

            .dropdown {
                position: absolute;
                bottom: 100%;
                right: 0;
                min-width: 150px;
                background: white;
                border: 1px solid var(--border-color);
                border-radius: 4px;
                box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
                z-index: 100;
                margin-bottom: 0.25rem;
                max-height: 200px;
                overflow-y: auto;
                display: none;
            }

            :host([open]) .dropdown {
                display: block;
            }

            .tracks-list {
                padding: 0.25rem;
            }

            .track-item {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0.5rem;
                border-radius: 4px;
                cursor: pointer;
                transition: background 0.15s;
            }

            .track-item:hover {
                background: rgba(0, 0, 0, 0.05);
            }

            .track-item.empty {
                justify-content: center;
                color: #999;
                font-style: italic;
                font-size: 0.75rem;
                cursor: default;
            }

            .track-item.empty:hover {
                background: none;
            }

            .track-checkbox {
                width: 18px;
                height: 18px;
                accent-color: var(--primary-color, #2563eb);
                cursor: pointer;
            }

            .track-name {
                flex: 1;
                font-size: 0.875rem;
                color: #333;
            }

            .track-item.disabled .track-name {
                color: #999;
                text-decoration: line-through;
            }

            .track-volume-slider {
                width: 60px;
                height: 4px;
                cursor: pointer;
                accent-color: var(--primary-color, #2563eb);
                margin-left: auto;
                flex-shrink: 0;
            }

            .track-volume-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: var(--primary-color, #2563eb);
                cursor: pointer;
            }

            .track-volume-slider::-moz-range-thumb {
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: var(--primary-color, #2563eb);
                cursor: pointer;
                border: none;
            }

            .track-mute-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                padding: 0;
                border: none;
                background: transparent;
                border-radius: 4px;
                cursor: pointer;
                font-size: 0.95rem;
                line-height: 1;
                flex-shrink: 0;
                opacity: 0.7;
                transition: opacity 0.15s, background 0.15s;
            }

            .track-mute-btn:hover {
                opacity: 1;
                background: rgba(0, 0, 0, 0.08);
            }

            .track-mute-btn.muted {
                opacity: 1;
                color: var(--primary-color, #2563eb);
            }

            .track-lyrics-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                padding: 0;
                border: none;
                background: transparent;
                border-radius: 4px;
                cursor: pointer;
                font-size: 0.75rem;
                font-weight: 600;
                line-height: 1;
                flex-shrink: 0;
                opacity: 0.7;
                transition: opacity 0.15s, background 0.15s;
                color: #555;
            }

            .track-lyrics-btn:hover:not(:disabled) {
                opacity: 1;
                background: rgba(0, 0, 0, 0.08);
            }

            .track-lyrics-btn.active {
                opacity: 1;
                color: var(--primary-color, #2563eb);
            }

            .track-lyrics-btn:disabled {
                opacity: 0.3;
                cursor: not-allowed;
            }

        `,
    ];

    @property({ type: String })
    prefix: 'conductor' | 'player' = 'conductor';

    @property({ attribute: false })
    tracks: TrackInfo[] = [];

    @property({ type: Boolean, reflect: true })
    open = false;

    @state()
    private _visibleIndexes: number[] = [];

    @state()
    private _volumes: Record<number, number> = {};

    @state()
    private _muted: Record<number, boolean> = {};

    @state()
    private _lyricsOnly: Record<number, boolean> = {};

    /** Unfiltered saved lyrics-only map. Kept separate from _lyricsOnly so
     *  entries for tracks without lyrics in this score survive a save. */
    private _savedLyricsOnly: Record<number, boolean> = {};

    private _boundCloseOnOutsideClick = this._closeOnOutsideClick.bind(this);

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('click', this._boundCloseOnOutsideClick);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('click', this._boundCloseOnOutsideClick);
    }

    private _closeOnOutsideClick(e: Event): void {
        if (!this.open) return;
        const path = e.composedPath();
        if (!path.includes(this)) {
            this.open = false;
        }
    }

    private _toggle(e: Event): void {
        e.stopPropagation();
        this.open = !this.open;
    }

    /**
     * When tracks property is set (score loaded), initialize from localStorage and fire initial events.
     */
    updated(changed: Map<string, unknown>): void {
        if (changed.has('tracks') && this.tracks.length > 0) {
            this._initializeFromStorage();
        }
    }

    private _initializeFromStorage(): void {
        // Load saved visibility
        const savedSelection = localStorage.getItem(`${this.prefix}VisibleTracks`);
        if (savedSelection) {
            try {
                const parsed = JSON.parse(savedSelection);
                this._visibleIndexes = parsed.filter(
                    (i: number) => i >= 0 && i < this.tracks.length
                );
                if (this._visibleIndexes.length === 0) {
                    this._visibleIndexes = this.tracks.map(t => t.index);
                }
            } catch {
                this._visibleIndexes = this.tracks.map(t => t.index);
            }
        } else {
            this._visibleIndexes = this.tracks.map(t => t.index);
        }

        // Load saved volumes
        const savedVolumesStr = localStorage.getItem(`${this.prefix}TrackVolumes`);
        if (savedVolumesStr) {
            try {
                this._volumes = JSON.parse(savedVolumesStr);
            } catch {
                this._volumes = {};
            }
        } else {
            this._volumes = {};
        }

        // Load saved mute state
        const savedMutedStr = localStorage.getItem(`${this.prefix}MutedTracks`);
        if (savedMutedStr) {
            try {
                this._muted = JSON.parse(savedMutedStr);
            } catch {
                this._muted = {};
            }
        } else {
            this._muted = {};
        }

        // Load saved lyrics-only state. Only apply it to tracks that carry
        // lyrics in this score, but keep the full saved map so switching to a
        // score without lyrics at that index doesn't erase the setting.
        const savedLyricsOnlyStr = localStorage.getItem(`${this.prefix}LyricsOnlyTracks`);
        let restored: Record<number, boolean> = {};
        if (savedLyricsOnlyStr) {
            try {
                restored = JSON.parse(savedLyricsOnlyStr);
            } catch {
                restored = {};
            }
        }
        this._savedLyricsOnly = restored;
        const validated: Record<number, boolean> = {};
        for (const track of this.tracks) {
            if (restored[track.index] && track.hasLyrics) {
                validated[track.index] = true;
            }
        }
        this._lyricsOnly = validated;

        // Fire initial events so consumer can apply state
        this._fireVisibilityChange();
        // Fire initial volume events for each track
        for (const track of this.tracks) {
            const vol = this._volumes[track.index] ?? 100;
            this._fireVolumeChange(track.index, vol);
        }
        // Fire initial mute events for each track
        for (const track of this.tracks) {
            this._fireMuteChange(track.index, this._muted[track.index] ?? false);
        }
        // Fire initial lyrics-only events so alphaTab re-applies the flag on score reload
        for (const track of this.tracks) {
            const on = this._lyricsOnly[track.index] ?? false;
            if (on) this._fireLyricsOnlyChange(track.index, true);
        }
    }

    private _handleVisibilityChange(trackIndex: number, checked: boolean): void {
        let newIndexes = [...this._visibleIndexes];

        if (checked) {
            if (!newIndexes.includes(trackIndex)) {
                newIndexes.push(trackIndex);
                newIndexes.sort((a, b) => a - b);
            }
        } else {
            newIndexes = newIndexes.filter(i => i !== trackIndex);
        }

        // Enforce at least one visible track
        if (newIndexes.length === 0) {
            return;
        }

        this._visibleIndexes = newIndexes;
        localStorage.setItem(`${this.prefix}VisibleTracks`, JSON.stringify(newIndexes));
        this._fireVisibilityChange();
    }

    private _handleVolumeInput(trackIndex: number, e: Event): void {
        e.stopPropagation();
        const volume = parseInt((e.target as HTMLInputElement).value, 10);
        this._volumes = { ...this._volumes, [trackIndex]: volume };
        localStorage.setItem(`${this.prefix}TrackVolumes`, JSON.stringify(this._volumes));
        this._fireVolumeChange(trackIndex, volume);
    }

    private _handleVolumeClick(e: Event): void {
        e.stopPropagation();
    }

    private _handleMuteToggle(trackIndex: number, e: Event): void {
        e.stopPropagation();
        const isMuted = !(this._muted[trackIndex] ?? false);
        this._muted = { ...this._muted, [trackIndex]: isMuted };
        localStorage.setItem(`${this.prefix}MutedTracks`, JSON.stringify(this._muted));
        this._fireMuteChange(trackIndex, isMuted);
    }

    private _handleLyricsOnlyToggle(trackIndex: number, e: Event): void {
        e.stopPropagation();
        const on = !(this._lyricsOnly[trackIndex] ?? false);
        this._lyricsOnly = { ...this._lyricsOnly, [trackIndex]: on };
        this._savedLyricsOnly = { ...this._savedLyricsOnly, [trackIndex]: on };
        localStorage.setItem(`${this.prefix}LyricsOnlyTracks`, JSON.stringify(this._savedLyricsOnly));
        this._fireLyricsOnlyChange(trackIndex, on);
    }

    private _handleTrackItemClick(trackIndex: number, e: Event): void {
        const target = e.target as HTMLElement;
        if (
            target.classList.contains('track-checkbox') ||
            target.classList.contains('track-volume-slider') ||
            target.classList.contains('track-mute-btn') ||
            target.classList.contains('track-lyrics-btn')
        ) return;
        // Toggle the checkbox
        const isVisible = this._visibleIndexes.includes(trackIndex);
        this._handleVisibilityChange(trackIndex, !isVisible);
    }

    private _fireVisibilityChange(): void {
        this.dispatchEvent(
            new CustomEvent('track-visibility-change', {
                bubbles: true,
                composed: true,
                detail: { visibleIndexes: [...this._visibleIndexes] },
            })
        );
    }

    private _fireVolumeChange(trackIndex: number, volume: number): void {
        this.dispatchEvent(
            new CustomEvent('track-volume-change', {
                bubbles: true,
                composed: true,
                detail: { trackIndex, volume },
            })
        );
    }

    private _fireMuteChange(trackIndex: number, muted: boolean): void {
        this.dispatchEvent(
            new CustomEvent('track-mute-change', {
                bubbles: true,
                composed: true,
                detail: { trackIndex, muted },
            })
        );
    }

    private _fireLyricsOnlyChange(trackIndex: number, lyricsOnly: boolean): void {
        this.dispatchEvent(
            new CustomEvent('track-lyrics-only-change', {
                bubbles: true,
                composed: true,
                detail: { trackIndex, lyricsOnly },
            })
        );
    }

    /** Public getter for current visible indexes */
    get visibleIndexes(): number[] {
        return [...this._visibleIndexes];
    }

    render() {
        return html`
            <button
                class="toggle-btn ${this.open ? 'active' : ''}"
                @click=${this._toggle}
                aria-expanded=${this.open}
                aria-haspopup="listbox"
            >
                <span class="tracks-icon">${'\u266B'}</span>
                <span>Tracks</span>
                <span class="tracks-arrow">${'\u25B2'}</span>
            </button>
            <div class="dropdown" role="listbox">
                <div class="tracks-list">
                    ${this.tracks.length === 0
                        ? html`<div class="track-item empty">Loading tracks...</div>`
                        : this.tracks.map(track => {
                            const isChecked = this._visibleIndexes.includes(track.index);
                            const volume = this._volumes[track.index] ?? 100;
                            const isMuted = this._muted[track.index] ?? false;
                            const isLyricsOnly = this._lyricsOnly[track.index] ?? false;
                            const canLyricsOnly = !!track.hasLyrics;
                            const lyricsTitle = canLyricsOnly
                                ? (isLyricsOnly ? 'Show music notes' : 'Show lyrics only (hide music notes)')
                                : 'This track has no lyrics';
                            return html`
                                <div
                                    class="track-item ${isChecked ? '' : 'disabled'}"
                                    @click=${(e: Event) => this._handleTrackItemClick(track.index, e)}
                                >
                                    <input
                                        type="checkbox"
                                        class="track-checkbox"
                                        .checked=${isChecked}
                                        @change=${(e: Event) => this._handleVisibilityChange(track.index, (e.target as HTMLInputElement).checked)}
                                    >
                                    <span class="track-name">${track.name}</span>
                                    <button
                                        class="track-lyrics-btn ${isLyricsOnly ? 'active' : ''}"
                                        title=${lyricsTitle}
                                        aria-label=${lyricsTitle}
                                        aria-pressed=${isLyricsOnly}
                                        ?disabled=${!canLyricsOnly}
                                        @click=${(e: Event) => this._handleLyricsOnlyToggle(track.index, e)}
                                    >Ly</button>
                                    <button
                                        class="track-mute-btn ${isMuted ? 'muted' : ''}"
                                        title=${isMuted ? 'Unmute' : 'Mute'}
                                        aria-label=${isMuted ? 'Unmute track' : 'Mute track'}
                                        aria-pressed=${isMuted}
                                        @click=${(e: Event) => this._handleMuteToggle(track.index, e)}
                                    >${isMuted ? '\u{1F507}' : '\u{1F508}'}</button>
                                    <input
                                        type="range"
                                        class="track-volume-slider"
                                        min="0"
                                        max="100"
                                        .value=${String(volume)}
                                        title="Volume: ${volume}%"
                                        @input=${(e: Event) => this._handleVolumeInput(track.index, e)}
                                        @click=${this._handleVolumeClick}
                                    >
                                </div>
                            `;
                        })
                    }
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'tracks-control': TracksControl;
    }
}
